import { type Module, type ModuleTree, type UnlockStatus } from '@courses/types-layer/types/module'
import { type Post } from '@courses/types-layer/types/post'
import {
  ASSESSMENT_DEFAULT_IMAGE,
  DEFAULT_CERTIFICATE_THUMBNAIL,
  DEFAULT_POSTER_IMAGE,
  DEFAULT_QUIZ_THUMBNAIL,
} from '@courses/utility-layer/constants'
import { PostContentType } from '@courses/utility-layer/enums'
import { numberToAlphabet, replaceBucketUrlWithCdnUrl } from '@courses/utility-layer/helper'
import dayjs from 'dayjs'

import { formatDate } from '@common/core-layer/utils/date.utils'

import type { Category, TreeNode } from '@courses/types-layer/types/category'
import type { DripUnlockInfo } from '../types/drip-gate'
import type { CurriculumChild } from '../types/use-modules'

function isCategoryNode(node: ModuleTree | Post): node is ModuleTree {
  return 'children' in node && Array.isArray((node as ModuleTree).children)
}

/**
 * Parses `metadata.dripDate` into a valid dayjs or `null`. Centralizes the
 * `raw != null && trim() !== ''` + `isValid()` guard used by the curriculum
 * description path and the drip-accessibility walker.
 */
function parseDripDate(raw: unknown): dayjs.Dayjs | null {
  if (raw == null || String(raw).trim() === '') return null
  const parsed = dayjs(String(raw))
  return parsed.isValid() ? parsed : null
}

export interface DripAccessibilitySummary {
  /** Incomplete posts in a non-dripped branch — what the learner can do next. */
  accessibleIncomplete: number
  /** Incomplete posts across the entire tree (used for badges/progress). */
  totalIncomplete: number
  /** Completed posts in a non-dripped branch — lessons the learner can resume. */
  accessibleCompletedCount: number
  /** Earliest unlock signal across dripped categories — date preferred, days fallback. */
  earliestDripUnlock: DripUnlockInfo | null
}

/**
 * Single-pass walker that computes everything `useDripGate` needs in one DFS:
 * accessible/total incomplete counts, accessible-completed count, and the earliest
 * drip-unlock signal. Replaces three independent walkers that each traversed
 * the same `moduleTree`.
 *
 * Drip propagation: a category is "branch-dripped" if itself or any ancestor has
 * `dripCategoryUnlockedMap[id] === false`. Posts inside a branch-dripped subtree
 * are invisible to `accessibleIncomplete` / `accessibleCompletedCount`.
 */
export function summarizeDripAccessibility(
  nodes: (ModuleTree | Post)[] | undefined,
  dripCategoryUnlockedMap: Record<string, boolean>,
  completedLessonIds: Set<string>
): DripAccessibilitySummary {
  let accessibleIncomplete = 0
  let totalIncomplete = 0
  let accessibleCompletedCount = 0
  let earliestDate: dayjs.Dayjs | null = null
  let smallestDays: number | null = null

  const walk = (items: (ModuleTree | Post)[] | undefined, ancestorDripped: boolean): void => {
    if (!items?.length) return

    for (const node of items) {
      if (!isCategoryNode(node)) {
        const isCompleted = completedLessonIds.has(node.id)
        if (isCompleted) {
          if (!ancestorDripped) accessibleCompletedCount += 1
        } else {
          totalIncomplete += 1
          if (!ancestorDripped) accessibleIncomplete += 1
        }
        continue
      }

      const selfDripped = dripCategoryUnlockedMap[node.id] === false
      if (selfDripped) {
        const dripDate = parseDripDate(node.metadata?.dripDate)
        if (dripDate) {
          if (!earliestDate || dripDate.isBefore(earliestDate)) {
            earliestDate = dripDate
          }
        } else if (typeof node.dripDays === 'number' && node.dripDays > 0) {
          if (smallestDays === null || node.dripDays < smallestDays) {
            smallestDays = node.dripDays
          }
        }
      }

      walk(node.children, ancestorDripped || selfDripped)
    }
  }

  walk(nodes, false)

  let earliestDripUnlock: DripUnlockInfo | null = null
  if (earliestDate) earliestDripUnlock = { kind: 'date', date: earliestDate }
  else if (smallestDays !== null) earliestDripUnlock = { kind: 'days', days: smallestDays }

  return { accessibleIncomplete, totalIncomplete, accessibleCompletedCount, earliestDripUnlock }
}

/**
 * Normalize GET /offers/categories/:productId (or equivalent) payloads to a flat {@link Category} list
 * for {@link buildCategoryTree} — same curriculum shape as checkout (`CourseCheckoutIndex` + product modules).
 */
export function normalizeOffersCategoriesPayload(data: unknown): Category[] {
  if (!data) return []
  if (Array.isArray(data)) return data as Category[]
  if (typeof data === 'object' && data !== null && 'categories' in data && Array.isArray((data as { categories: unknown }).categories)) {
    return (data as { categories: Category[] }).categories
  }
  return []
}

/** Total published posts attached to top-level category rows (catalog / offer payload stats). */
export function totalPostsInCategories(categories: Category[]): number {
  return categories.reduce((sum, c) => sum + (c.posts?.length ?? 0), 0)
}

export interface ListItem {
  id: string
  title: string
  type: 'category' | 'post'
  moduleId?: string
  contentType?: string
  letter?: string
  children: ListItem[]
  isExpanded?: boolean
  showSideLine?: boolean
  posterImage?: string | null
  status?: string
  isSelected?: boolean
  number?: number
  lockedByPost?: string
  lockedByCategory?: string
  isCertificate?: boolean
  hideContent?: boolean
  description?: string | null
  userPurchaseDate?: string | null
  dripDays?: number
  metadata?: Record<string, unknown> | null
  totalChildren?: number
}

/**
 * Recursively counts only actual lesson/post nodes in a tree.
 * Sub-categories are traversed but NOT counted — only their nested lessons contribute.
 */
export function countLessonPosts(nodes: (ModuleTree | Post | TreeNode)[] | undefined): number {
  if (!nodes?.length) return 0
  let count = 0
  for (const node of nodes) {
    if (node.type === 'category') {
      const ch = 'children' in node ? node.children : undefined
      if (Array.isArray(ch) && ch.length) {
        count += countLessonPosts(ch as (ModuleTree | Post | TreeNode)[])
      } else {
        count += (node as ModuleTree).totalChildren ?? 0
      }
    } else {
      count += 1
    }
  }
  return count
}

/**
 * CDN-rewritten `posterImage`, falling back to content-type defaults
 * (quiz / assignment / generic). Shared by the curriculum tree and autoplay card.
 */
export function getPosterImage(post: Post | ModuleTree | TreeNode): string {
  if (post.contentType === 'quiz') {
    return replaceBucketUrlWithCdnUrl(post.posterImage) || DEFAULT_QUIZ_THUMBNAIL
  }
  if (post.contentType === 'assignment') {
    return replaceBucketUrlWithCdnUrl(post.posterImage) || ASSESSMENT_DEFAULT_IMAGE
  }
  return replaceBucketUrlWithCdnUrl(post.posterImage) || DEFAULT_POSTER_IMAGE
}

export function buildCategoryTree(categories: Category[]): TreeNode[] {
  if (!categories || categories.length === 0) return []

  // Create a map for quick category lookup
  const categoryMap: Record<string, Category> = {}
  categories.forEach((category: Category) => {
    categoryMap[category.id!] = category
  })

  // Store top-level categories
  const rootCategories: TreeNode[] = []

  // First pass: create all category nodes
  const categoryNodes: Record<string, TreeNode> = {}

  categories.forEach((category: Category) => {
    const categoryNode: TreeNode = {
      id: category.id!,
      title: category.title!,
      description: category.description,
      type: 'category',
      sequenceNo: category.sequenceNo || 0,
      visibility: category.visibility,
      dripDays: category.dripDays,
      posterImage: category.posterImage,
      productId: category.productId,
      hasProductAccess: category.hasProductAccess,
      available: category.available,
      isLocked: !!(category.lockedBy || category.lockedByCategory),
      lockedBy: category.lockedBy,
      lockedByCategory: category.lockedByCategory,
      metadata: category.metadata,
      userPurchaseDate: category.userPurchaseDate,
      children: [],
    }

    categoryNodes[category.id!] = categoryNode
  })

  // Second pass: build the tree structure and add posts
  categories.forEach((category: Category) => {
    const categoryNode = categoryNodes[category.id!]

    const userPurchaseDate = dayjs(categoryNode.userPurchaseDate)
    const actualContentAvailableDate = userPurchaseDate.add(categoryNode.dripDays || 0, 'days')
    const dripDifference = dayjs(userPurchaseDate).diff(actualContentAvailableDate, 'days')
    const dripDateDifference = dayjs(categoryNode.metadata?.dripDate || '').diff(dayjs(), 'days')

    const isDripped = dripDifference > 0 || dripDateDifference > 0

    // Add posts as children
    if (category.posts && category.posts.length > 0 && !isDripped) {
      const postNodes: TreeNode[] = category.posts.map((post: Post) => ({
        id: post.id!,
        title: post.title!,
        description: post.description,
        type: 'post' as const,
        sequenceNo: post.sequenceNo || 0,
        visibility: post.visibility,
        posterImage: getPosterImage(post),
        productId: post.productId,
        commentStatus: post.commentStatus,
        contentType: post.contentType,
        isLocked: !!(post.lockedByPost || post.lockedByCategory),
        lockedByPost: post.lockedByPost,
        lockedByCategory: post.lockedByCategory,
        metaData: post.metaData,
        certificateTemplateId: post.certificateTemplateId || '',
        children: [], // Posts don't have children
      }))

      // Sort posts by sequenceNo
      postNodes.sort((a, b) => a.sequenceNo - b.sequenceNo)
      categoryNode.children.push(...postNodes)
    }

    // Handle parent-child relationships
    if (category.parentCategory && categoryMap[category.parentCategory] && !isDripped) {
      // This is a subcategory, add it to parent's children
      const parentNode = categoryNodes[category.parentCategory]
      if (parentNode) {
        parentNode.children.push(categoryNode)
      }
    } else {
      // This is a root category
      rootCategories.push(categoryNode)
    }
  })

  // Recursively sort children by sequenceNo
  function sortChildrenRecursively(node: TreeNode): TreeNode {
    // Sort children by sequenceNo, categories first, then posts
    node.children.sort((a, b) => {
      // Within same type, sort by sequenceNo
      return a.sequenceNo - b.sequenceNo
    })

    // Recursively sort subcategories
    node.children.forEach(child => {
      if (child.type === 'category' && child.children.length > 0) {
        sortChildrenRecursively(child)
      }
    })

    return node
  }

  // Sort root categories and their children
  return rootCategories.map(sortChildrenRecursively).sort((a, b) => a.sequenceNo - b.sequenceNo)
}

// export function buildContentLookUp(modules: ModuleTree[]): Map<string, ModuleTree> {
//   return modules.reduce((acc, module) => {
//     acc.set(module.id, module)
//     if (module.children.length > 0) {
//       buildContentLookUp(module.children as ModuleTree[])
//     }
//     return acc
//   }, new Map<string, ModuleTree>())
// }

function getContentType(post: Post): string {
  if (post.contentType === PostContentType.video && !(post.embedJson || post.embedded_media) && post.video?.url) {
    return PostContentType.video
  }
  if (post.contentType === PostContentType.audio) {
    return PostContentType.audio
  }
  if (post.contentType === PostContentType.video && (post.embedJson || post.embedded_media)) {
    return PostContentType.embed
  }
  if (post.contentType === PostContentType.video && !(post.embedJson || post.embedded_media) && !post.video?.url) {
    return PostContentType.text
  }
  return post.contentType
}

export function buildModuleTree(
  modules: Module[] | undefined,
  lessonResults: (
    | {
        lessons: Post[]
        totalLessons: number
      }
    | undefined
  )[]
): ModuleTree[] {
  if (!modules?.length) {
    return []
  }

  const modulesWithLessons: { module: Module; lessons: Post[] }[] = modules.map((module, index) => {
    return {
      module,
      type: 'category',
      lessons: lessonResults[index]?.lessons ?? [],
    }
  })

  const moduleTreeMap: Map<string, ModuleTree> = new Map()
  for (const { module } of modulesWithLessons) {
    moduleTreeMap.set(module.id, {
      ...module,
      type: 'category',
      lockedBy: module.lockedBy,
      lockedByPost: module.lockedByPost || null,
      lockedByCategory: module.lockedByCategory,
      certificateTemplateId: module.certificateTemplateId || null,
      totalChildren: module.lessonsCount ?? 0,
      children: [],
    })
  }

  const rootNodes: ModuleTree[] = []
  for (const { module, lessons } of modulesWithLessons) {
    const moduleNode = moduleTreeMap.get(module.id)
    if (!moduleNode) continue

    if (lessons.length) {
      const sortedLessons = [...lessons].sort((a, b) => (a.sequenceNo ?? 0) - (b.sequenceNo ?? 0))
      moduleNode.children.push(...sortedLessons)
    }

    if (module.parentCategory && moduleTreeMap.has(module.parentCategory)) {
      const parentNode = moduleTreeMap.get(module.parentCategory)
      parentNode?.children.push(moduleNode)
    } else {
      rootNodes.push(moduleNode)
    }
  }

  for (const node of moduleTreeMap.values()) {
    node.children.sort(
      (a, b) =>
        ((a as ModuleTree).sequenceNo ?? (a as Post).sequenceNo ?? 0) - ((b as ModuleTree).sequenceNo ?? (b as Post).sequenceNo ?? 0)
    )
  }
  rootNodes.sort((a, b) => a.sequenceNo - b.sequenceNo)

  return rootNodes
}

/**
 * True when every completable lesson is at 100%; cert posts skipped, empty trees return false.
 * Raw `(ModuleTree | Post)[]` shape (pre-transform) — uses `post.certificateTemplateId`.
 * Post-transform counterpart is `areAllCompletableChildrenComplete`; both must stay in lockstep.
 */
export function areCategoryLessonsCompleted(
  nodes: (ModuleTree | Post)[] | undefined,
  completedPostsMap: Record<string, { percentage?: number }>
): boolean {
  if (!nodes?.length) return false

  let hasCompletableLesson = false

  const walk = (list: (ModuleTree | Post)[]): boolean => {
    for (const node of list) {
      if (node.type === 'category') {
        const children = 'children' in node ? node.children : undefined
        if (Array.isArray(children) && children.length) {
          if (!walk(children as (ModuleTree | Post)[])) return false
        }
        continue
      }

      const post = node as Post
      if (post.certificateTemplateId) continue

      hasCompletableLesson = true
      if (completedPostsMap[post.id]?.percentage !== 100) return false
    }
    return true
  }

  const allDone = walk(nodes)
  return allDone && hasCompletableLesson
}

/**
 * True when every non-certificate descendant is completed; empty trees return false.
 * Menu-options shape (post-transform) — uses `child.isCertificate`. Pre-transform counterpart
 * is `areCategoryLessonsCompleted`; both signals are derived from `certificateTemplateId` in
 * `getMenuOptionsForModuleTree`, so they must be updated in lockstep if that derivation changes.
 */
export function areAllCompletableChildrenComplete(children: CurriculumChild[]): boolean {
  if (!children?.length) return false
  let hasCompletableLesson = false
  for (const child of children) {
    if (child.isCertificate) continue
    if (child.children?.length) {
      if (!areAllCompletableChildrenComplete(child.children)) return false
      continue
    }
    hasCompletableLesson = true
    if (child.status !== 'completed') return false
  }
  return hasCompletableLesson
}

export function buildModuleTreeLookUp(nodes: (ModuleTree | Post)[]): Map<string, ModuleTree | Post> {
  const lookUp = new Map<string, ModuleTree | Post>()

  function traverse(node: ModuleTree | Post) {
    lookUp.set(node.id, { ...node })
    if ('children' in node && node.children) {
      node.children.forEach(traverse)
    }
  }

  nodes.forEach(traverse)
  return lookUp
}

/** Type guard separating category nodes from `Post` leaves. */
export function isModuleTreeCategoryNode(node: ModuleTree | Post | undefined): node is ModuleTree {
  return node !== undefined && (node as ModuleTree).type === 'category'
}

/**
 * Flatten the curriculum tree to an ordered `Post[]` (display order, leaves only).
 * Does not filter certificates; consumers must filter on `certificateTemplateId` if needed.
 */
export function flattenModuleTreeToPosts(nodes: (ModuleTree | Post)[] | undefined): Post[] {
  if (!nodes?.length) return []
  const result: Post[] = []
  const walk = (list: (ModuleTree | Post)[]) => {
    for (const node of list) {
      if (isModuleTreeCategoryNode(node)) {
        const children = node.children
        if (Array.isArray(children) && children.length) {
          walk(children as (ModuleTree | Post)[])
        }
        continue
      }
      result.push(node)
    }
  }
  walk(nodes)
  return result
}

/** Date wins when set; otherwise pluralized days. */
function getDripCategoryDescription(mod: ModuleTree, t: (key: string, values?: Record<string, unknown>) => string): string {
  const parsed = parseDripDate(mod.metadata?.dripDate)
  if (parsed) {
    return t('categoriesLayer.dripDate', { date: formatDate(parsed.toISOString()) })
  }
  const days = mod.dripDays ?? 0
  return t('categoriesLayer.dripDays', { days, plural: days === 1 ? '' : 's' })
}

export function getMenuOptionsForModuleTree({
  nodes,
  currentPostId,
  dripCategoryUnlockedMap,
  categoryUnlockedMap,
  postUnlockedMap,
  t,
  level = 0,
  isExpanded = false,
}: {
  nodes: (ModuleTree | Post | TreeNode)[] | undefined
  currentPostId?: string
  dripCategoryUnlockedMap?: Record<string, boolean>
  categoryUnlockedMap?: Record<string, UnlockStatus>
  postUnlockedMap?: Record<string, UnlockStatus>
  t: (key: string, values?: Record<string, unknown>) => string
  level?: number
  isExpanded?: boolean
}): ListItem[] {
  if (!nodes) {
    return []
  }

  return nodes.map((node, index) => {
    const isPost = node.type !== 'category'
    const nodeType = isPost ? 'post' : 'category'

    const menuItem: ListItem = {
      id: node.id,
      title: node.title,
      type: nodeType,
      contentType: isPost ? getContentType(node as Post) : undefined,
      moduleId: isPost ? (node as Post).categoryId : undefined,
      letter: numberToAlphabet(index),
      children: [],
      isExpanded: isExpanded || currentPostId === node.id,
      showSideLine: level > 0,
      posterImage: isPost ? getPosterImage(node) : node.posterImage,
      status: '',
      isSelected: node.id === currentPostId,
      number: index + 1,
      lockedByPost: isPost ? node.lockedByPost || '' : '',
      lockedByCategory: ('lockedByCategory' in node && node.lockedByCategory) || '',
      isCertificate: isPost && node.certificateTemplateId ? true : false,
      totalChildren: (!isPost && (node as ModuleTree).totalChildren) || 0,
    }

    if (isPost) {
      const unlockEntry = postUnlockedMap?.[node.id]
      if (unlockEntry) {
        if (!unlockEntry.isUnlocked) {
          menuItem.status = 'locked'
        }
      } else if (node.visibility !== 'published' && (node.lockedByPost || node.lockedByCategory)) {
        menuItem.status = 'locked'
      }
    } else {
      const mod = node as ModuleTree
      if (dripCategoryUnlockedMap?.[node.id] === false) {
        menuItem.hideContent = true
        menuItem.description = getDripCategoryDescription(mod, t)
      }

      const categoryUnlockEntry = categoryUnlockedMap?.[node.id]
      if (categoryUnlockEntry) {
        if (!categoryUnlockEntry.isUnlocked) {
          menuItem.status = 'locked'
        }
      } else if ('lockedBy' in node && node.lockedBy) {
        menuItem.status = 'locked'
      }
    }

    if (isPost && node.certificateTemplateId) {
      menuItem.posterImage = DEFAULT_CERTIFICATE_THUMBNAIL
      menuItem.status = 'locked'
    }

    if ('children' in node && node.children?.length > 0) {
      const children = getMenuOptionsForModuleTree({
        nodes: node.children,
        currentPostId,
        dripCategoryUnlockedMap,
        categoryUnlockedMap,
        postUnlockedMap,
        t,
        level: level + 1,
        isExpanded,
      })
      menuItem.children = children
      if (!isPost) {
        const recursiveCount = countLessonPosts(node.children as (ModuleTree | Post | TreeNode)[])
        menuItem.totalChildren = Math.max(menuItem.totalChildren ?? 0, recursiveCount)
      }
      menuItem.isExpanded = children.some(child => child.isExpanded)
    }

    return menuItem
  })
}
